import { computed, DestroyRef, Directive, inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { connectState } from '@examdojo/angular/util';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { QuestionStatus } from '@examdojo/models/question';
import { ImageEditingType, QuestionEditingOptions } from '@examdojo/models/question-editor';
import {
  ChecklistConfig,
  countMarkschemes,
  countTotalImages,
  extractImageInfo,
  extractMarkschemeInfo,
  extractMCQInfo,
  extractTableInfo,
  Feedback,
  QuestionStoreModel,
} from '@examdojo/question';
import {
  createDynamicSurveyConfig,
  DerivativeQuestionReviewType,
  Evaluation,
  QUESTION_VALIDATION_STATUS_STRATEGY_MAPPING,
  QuestionReviewFeedbackFormState,
  QuestionType,
  REVIEW_ACTION_TYPE_TO_DERIVATIVE_QUESTION_REVIEW_TYPE,
  ReviewItemService,
} from '@examdojo/review';
import { UserQuery } from '@examdojo/user';
import baseConfig from '@settings/ibdp-derivative-question-checklist.json';
import { combineLatest, distinctUntilChanged, EMPTY, map, startWith, switchMap } from 'rxjs';
import { shouldShowDerivativeQuestionConsensusWarning } from '../../shared/consensus-utils';
import { DerivativeQuestionEditorItemsFormService } from '../derivative-question-editor-items-form-service';
import { getQuestionReviewConfig } from '../derivative-question-validation.model';

@Directive()
export abstract class ReviewModeBaseComponent {
  protected readonly errorHandlerService = inject(ErrorHandlerService);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly DerivativeQuestionReviewType = DerivativeQuestionReviewType;
  protected readonly questionEditorItemsFormService = inject(DerivativeQuestionEditorItemsFormService);
  protected readonly reviewItemService = inject(ReviewItemService);
  protected readonly userQuery = inject(UserQuery);

  readonly formStateChange = output<QuestionReviewFeedbackFormState>();

  readonly skip = output();
  readonly feedback = output<Feedback[]>();

  readonly calculatorAllowed = input.required<boolean>();

  readonly reviewType = input.required<DerivativeQuestionReviewType>();
  readonly question = input.required<QuestionStoreModel>();

  readonly isSubmitting = input(false);
  readonly checklistConfig = baseConfig as ChecklistConfig;

  readonly totalImages = computed(() => countTotalImages(this.question().question_items ?? []));
  readonly totalMarkschemes = computed(() => countMarkschemes(this.question().question_items ?? []));
  readonly imageInfo = computed(() => extractImageInfo(this.question().question_items ?? []));
  readonly markschemeInfo = computed(() => extractMarkschemeInfo(this.question().question_items ?? []));
  readonly mcqInfo = computed(() => extractMCQInfo(this.question().question_items ?? []));
  readonly tableInfo = computed(() => extractTableInfo(this.question().question_items ?? []));

  // Convert editing options observable to signal
  readonly editingOptions = toSignal<QuestionEditingOptions | null, QuestionEditingOptions | null>(
    this.questionEditorItemsFormService.editingOptions$,
    {
      initialValue: {
        allowImageEditing: [ImageEditingType.Original, ImageEditingType.Generated],
        allowQuestionEditing: true,
        allowMarkschemeEditing: true,
        allowMcqEditing: false,
        allowMarkschemeStructureEditing: false,
        allowMcqStructureEditing: false,
        hideBadges: false,
        allowQuestionStructureEditing: false,
      },
    },
  );

  // Computed properties for individual editing options
  readonly allowImageEditing = computed(() => (this.editingOptions()?.allowImageEditing?.length ?? 0) > 0);
  readonly allowQuestionEditing = computed(() => this.editingOptions()?.allowQuestionEditing ?? false);
  readonly allowMarkschemeEditing = computed(() => this.editingOptions()?.allowMarkschemeEditing ?? false);

  // Computed property to determine if any editing is allowed
  readonly editingAllowed = computed(
    () => this.allowImageEditing() || this.allowQuestionEditing() || this.allowMarkschemeEditing(),
  );

  readonly dynamicSurveyConfig = computed(() => {
    return createDynamicSurveyConfig(
      this.checklistConfig as ChecklistConfig<QuestionStatus>,
      this.markschemeInfo(),
      this.imageInfo(),
      this.mcqInfo(),
      this.tableInfo(),
      QUESTION_VALIDATION_STATUS_STRATEGY_MAPPING as Record<DerivativeQuestionReviewType, QuestionStatus>,
      this.reviewType(),
    );
  });

  // Previous feedback functionality - following reference question pattern
  readonly questionId$ = toObservable(this.question).pipe(
    map((question) => question.id),
    distinctUntilChanged(),
  );

  readonly previousEvaluations$ = this.questionId$.pipe(
    switchMap((questionId) => (questionId ? this.reviewItemService.getQuestionFeedback(questionId) : EMPTY)),
    this.errorHandlerService.catchHttpErrors(() => EMPTY),
  );

  readonly config = computed(() => getQuestionReviewConfig(this.reviewType()));
  readonly showPreviousFeedback = computed(() => this.config().showPreviousFeedback);

  readonly state = connectState({
    showConsensusWarning: combineLatest([
      this.previousEvaluations$,
      this.userQuery.active$,
      toObservable(this.reviewType),
    ]).pipe(
      map(([evaluations, user, reviewType]) =>
        user?.id ? shouldShowDerivativeQuestionConsensusWarning(evaluations, user.id, reviewType) : false,
      ),
    ),
    previousEvaluations: combineLatest([toObservable(this.showPreviousFeedback), this.previousEvaluations$]).pipe(
      map(([showPreviousFeedback, evaluations]) =>
        evaluations
          .filter((e) => {
            const reviewType = REVIEW_ACTION_TYPE_TO_DERIVATIVE_QUESTION_REVIEW_TYPE[e.action_type];

            if (!reviewType) {
              return false;
            }

            const shouldInclude = showPreviousFeedback.includes(reviewType);
            return shouldInclude;
          })
          .map((e) => {
            const reviewType = REVIEW_ACTION_TYPE_TO_DERIVATIVE_QUESTION_REVIEW_TYPE[e.action_type]!;

            const dynamicSurveyConfig = createDynamicSurveyConfig(
              this.checklistConfig as ChecklistConfig<QuestionStatus>,
              this.markschemeInfo(),
              this.imageInfo(),
              this.mcqInfo(),
              this.tableInfo(),
              QUESTION_VALIDATION_STATUS_STRATEGY_MAPPING as Record<DerivativeQuestionReviewType, QuestionStatus>,
              reviewType,
            );

            return {
              ...e,
              id: e.action_id,
              reviewType,
              config: dynamicSurveyConfig,
              questionType: QuestionType.Derivative as const,
            };
          }),
      ),
      startWith([] as Evaluation[]),
    ),
  });

  /**
   * Skip the current question
   */
  skipQuestion(): void {
    this.skip.emit();
  }
}
